<template>
  <div ref="componentRef" class="application-component file-viewer-component" data-selection-owner="file-viewer">
    <div class="application-component-title">{{ application?.name || '文件查看' }}</div>
    <div class="application-component-content">
      <div class="file-meta">
        <span class="file-meta-tag">{{ application?.sourceLabel || '文件类' }}</span>
        <span class="file-meta-name">{{ application?.fileName || application?.name || '未命名文件' }}</span>
        <button v-if="previewTarget" class="file-meta-action" type="button" @click="handlePreview">
          打开原文件
        </button>
      </div>

      <div
        v-if="hasParagraphs"
        class="paragraph file-viewer-paragraph"
        :style="{ fontSize }"
        @mouseup="handleTextSelection"
      >
        <div
          v-for="item in renderedParagraphs"
          :id="item.anchorId"
          :key="item.anchorId"
          class="paragraph-item textselection"
          :data-anchor-id="item.anchorId"
          :data-paragraph-index="item.index"
        >
          <p>
            <template v-for="segment in item.segments" :key="segment.key">
              <span :class="{ 'feature-highlight': segment.featureActive }">
                {{ segment.text }}
              </span>
            </template>
          </p>
        </div>
      </div>

      <div v-else class="file-empty">
        <div class="file-empty-title">该文件尚未解析为段落文本</div>
        <button v-if="previewTarget" class="file-empty-button" type="button" @click="handlePreview">
          先打开原文件
        </button>
      </div>
    </div>

    <div v-if="showToolbar" class="note-selection-toolbar" :style="toolbarStyle">
      <div @click="handleAIChat">AI对话引用</div>
      <div @click="handleAIExplain">AI释义</div>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue'
import { ElMessage } from 'element-plus'
import { useGlobalStore } from '@/store/modules/global'
import { fetchSearchNoveltyDocument } from '@/api/writing'
import { jsonParse } from '@/utils'

const FEATURE_BREAKDOWN_EVENT = 'feature-breakdown-focus'
const WORKSPACE_FILE_FOCUS_EVENT = 'workspace-file-focus'
const NOVELTY_FILE_ID_PREFIX = 'novelty-analysis:'
const CONTROL_CHAR_REGEX = /[\u0000-\u0008\u000B-\u001F\u007F]/g
const PAGE_FIELD_REGEX = /^PAGE\s+\d+$/i

const globalStore = useGlobalStore()

const decodeToken = (value = '') => {
  try {
    return decodeURIComponent(String(value || ''))
  } catch (error) {
    return String(value || '')
  }
}

const extractNoveltyPnFromToken = (value = '') => {
  const candidates = [String(value || ''), decodeToken(value)]
  for (const candidate of candidates) {
    const prefixIndex = candidate.indexOf(NOVELTY_FILE_ID_PREFIX)
    if (prefixIndex === -1) {
      continue
    }

    const pn = candidate.slice(prefixIndex + NOVELTY_FILE_ID_PREFIX.length).split(/[/?#&]/)[0].trim()
    if (pn) {
      return pn
    }
  }
  return ''
}

const props = defineProps({
  readOnly: {
    type: Boolean,
    default: false
  },
  data: {
    type: Object,
    default: () => null
  },
  application: {
    type: Object,
    default: () => null
  },
  projectId: {
    type: String,
    default: ''
  }
})

const componentRef = ref(null)
const activeFeatureRanges = ref([])
const showToolbar = ref(false)
const toolbarStyle = ref({
  top: '0px',
  left: '0px'
})
const selectedText = ref('')

const fontSize = computed(() => `${globalStore.fontSize}px`)
const previewTarget = computed(() => props.application?.previewPath || props.application?.fileUrl || '')
const noveltyDocumentPayload = computed(() => {
  const application = props.application || {}
  const fileMeta = application.fileMeta || {}
  const fileMetaId = String(fileMeta.id || '')
  const runtimeId = String(application.id || '')
  const pn = String(
    fileMeta.pn ||
    application.pn ||
    extractNoveltyPnFromToken(fileMetaId) ||
    extractNoveltyPnFromToken(runtimeId)
  ).trim()

  if (!pn) {
    return null
  }

  const isNoveltyDocument = fileMetaId.startsWith(NOVELTY_FILE_ID_PREFIX) ||
    runtimeId.includes(encodeURIComponent(NOVELTY_FILE_ID_PREFIX)) ||
    runtimeId.includes(NOVELTY_FILE_ID_PREFIX)

  if (!isNoveltyDocument) {
    return null
  }

  return {
    refCode: String(fileMeta.refCode || application.refCode || '').trim(),
    pn,
    semanticsScore: fileMeta.semanticsScore || application.semanticsScore
  }
})

const sanitizeParagraphText = (text = '') => {
  const normalized = String(text || '')
    .replace(/\r\n?/g, '\n')
    .replace(CONTROL_CHAR_REGEX, '')
    .replace(/\u00A0/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim()

  if (PAGE_FIELD_REGEX.test(normalized.replace(/\s+/g, ' '))) {
    return ''
  }

  return normalized
}

const paragraphList = computed(() => {
  const contentItems = jsonParse(props.data?.docResult || '[]')
  if (!Array.isArray(contentItems)) {
    return []
  }

  return contentItems
    .filter(item => item?.type === 'TEXT')
    .map((item, index) => {
      const paragraphIndex = item?.index ?? index
      const text = sanitizeParagraphText(item?.text || '')

      return {
        anchorId: `file-viewer-paragraph-${paragraphIndex}`,
        index: paragraphIndex,
        text,
      }
    })
    .filter(item => item.text)
})

const hasParagraphs = computed(() => paragraphList.value.length > 0)

const normalizeFeatureRanges = (value) => {
  if (!Array.isArray(value)) {
    return []
  }

  return value
    .map((item, index) => {
      const paragraphIndex = Number(item?.paragraphIndex)
      const startOffset = Number(item?.startOffset)
      const endOffset = Number(item?.endOffset)

      return {
        ...item,
        id: item?.id || `file-feature-range-${index}`,
        paragraphIndex: Number.isFinite(paragraphIndex) ? paragraphIndex : null,
        startOffset: Number.isFinite(startOffset) ? startOffset : null,
        endOffset: Number.isFinite(endOffset) ? endOffset : null,
      }
    })
    .filter(item =>
      Number.isInteger(item.paragraphIndex) &&
      Number.isInteger(item.startOffset) &&
      Number.isInteger(item.endOffset) &&
      item.endOffset > item.startOffset
    )
}

const buildParagraphSegments = (text, featureRanges) => {
  if (!text) {
    return []
  }

  const boundaries = new Set([0, text.length])
  featureRanges.forEach((range) => {
    boundaries.add(Math.max(0, Math.min(text.length, range.startOffset)))
    boundaries.add(Math.max(0, Math.min(text.length, range.endOffset)))
  })

  const orderedBoundaries = Array.from(boundaries).sort((left, right) => left - right)
  const segments = []

  for (let index = 0; index < orderedBoundaries.length - 1; index += 1) {
    const start = orderedBoundaries[index]
    const end = orderedBoundaries[index + 1]
    if (end <= start) {
      continue
    }

    const segmentText = text.slice(start, end)
    if (!segmentText) {
      continue
    }

    const featureActive = featureRanges.some(item => start >= item.startOffset && end <= item.endOffset)
    segments.push({
      key: `segment-${start}-${end}-${featureActive ? 'feature' : 'normal'}`,
      text: segmentText,
      featureActive,
    })
  }

  return segments.length ? segments : [{ key: `${text.length}-fallback`, text, featureActive: false }]
}

const renderedParagraphs = computed(() => {
  return paragraphList.value.map(item => {
    const paragraphFeatureRanges = activeFeatureRanges.value
      .filter(range =>
        range.paragraphIndex === item.index &&
        Number.isInteger(range.startOffset) &&
        Number.isInteger(range.endOffset) &&
        range.startOffset >= 0 &&
        range.endOffset > range.startOffset
      )
      .sort((left, right) => left.startOffset - right.startOffset)

    return {
      ...item,
      segments: buildParagraphSegments(item.text, paragraphFeatureRanges),
    }
  })
})

const clearSelection = () => {
  showToolbar.value = false
  selectedText.value = ''
  window.getSelection()?.removeAllRanges()
}

const getToolbarPosition = (rect) => {
  const containerRect = componentRef.value?.getBoundingClientRect()
  const toolbarWidth = 180
  const toolbarHeight = 36

  let left = rect.left + rect.width / 2
  let top = rect.bottom + 8

  if (containerRect) {
    const halfWidth = toolbarWidth / 2
    const minLeft = containerRect.left + halfWidth + 8
    const maxLeft = containerRect.right - halfWidth - 8
    left = Math.max(minLeft, Math.min(maxLeft, left))

    const maxTop = containerRect.bottom - toolbarHeight - 8
    if (top > maxTop) {
      top = Math.max(containerRect.top + 8, rect.top - toolbarHeight - 8)
    }
  }

  return {
    left: `${left}px`,
    top: `${top}px`,
  }
}

const handleTextSelection = () => {
  const selection = window.getSelection()

  if (!selection || selection.isCollapsed || !selection.toString().trim()) {
    clearSelection()
    return
  }

  selectedText.value = selection.toString().trim()
  toolbarStyle.value = getToolbarPosition(selection.getRangeAt(0).getBoundingClientRect())
  showToolbar.value = true
}

const handleAIChat = () => {
  if (!selectedText.value) {
    return
  }

  const quoteText = selectedText.value
  globalStore.setAIVisible(true)
  setTimeout(() => {
    globalStore.setQuoteAIChat({
      title: props.application?.name || '文件查看',
      text: quoteText,
    })
  }, 200)
  clearSelection()
}

const handleAIExplain = () => {
  if (!selectedText.value) {
    return
  }

  const quoteText = selectedText.value
  globalStore.setAIVisible(true)
  setTimeout(() => {
    globalStore.setSendAIChat(quoteText)
  }, 200)
  clearSelection()
}

const openPreviewTarget = (target) => {
  if (!target) {
    return false
  }
  window.open(target, '_blank')
  return true
}

const handlePreview = async () => {
  if (noveltyDocumentPayload.value && props.projectId) {
    try {
      const result = await fetchSearchNoveltyDocument(props.projectId, noveltyDocumentPayload.value)
      if (openPreviewTarget(result?.previewPath || result?.fileUrl)) {
        return
      }
    } catch (error) {
      console.error('refresh novelty document preview failed', error)
      if (previewTarget.value) {
        ElMessage.warning('实时获取失败，已尝试打开已有原文链接')
      }
    }
  }

  if (!previewTarget.value) {
    ElMessage.warning('暂无可预览文件')
    return
  }
  openPreviewTarget(previewTarget.value)
}

const scrollToFeatureRange = async (sourceRanges = []) => {
  const firstRange = sourceRanges[0]
  if (!firstRange) {
    return
  }

  await nextTick()

  const anchorElement = document.getElementById(`file-viewer-paragraph-${firstRange.paragraphIndex}`)
  if (!anchorElement) {
    return
  }

  anchorElement.scrollIntoView({
    behavior: 'smooth',
    block: 'center',
  })
}

const matchFocusTarget = (targetItemId = '') => {
  if (!targetItemId) {
    return false
  }
  return targetItemId === props.application?.id
}

const handleFeatureBreakdownFocus = (event) => {
  const { projectId, sourceRanges, itemId } = event.detail || {}
  if (projectId !== props.projectId) {
    return
  }
  if (itemId && !matchFocusTarget(itemId)) {
    return
  }
  if (!itemId) {
    return
  }

  const normalizedRanges = normalizeFeatureRanges(sourceRanges)
  activeFeatureRanges.value = normalizedRanges
  scrollToFeatureRange(normalizedRanges)
}

const handleDocumentMouseDown = (event) => {
  if (componentRef.value?.contains(event.target)) {
    return
  }
  clearSelection()
}

onMounted(() => {
  document.addEventListener('mousedown', handleDocumentMouseDown)
  window.addEventListener(FEATURE_BREAKDOWN_EVENT, handleFeatureBreakdownFocus)
  window.addEventListener(WORKSPACE_FILE_FOCUS_EVENT, handleFeatureBreakdownFocus)
})

onUnmounted(() => {
  document.removeEventListener('mousedown', handleDocumentMouseDown)
  window.removeEventListener(FEATURE_BREAKDOWN_EVENT, handleFeatureBreakdownFocus)
  window.removeEventListener(WORKSPACE_FILE_FOCUS_EVENT, handleFeatureBreakdownFocus)
})
</script>

<style scoped lang="scss">
@import './common.scss';

.file-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
  flex-wrap: wrap;
}

.file-meta-tag {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  color: #0f766e;
  font-size: 12px;
  font-weight: 600;
  background: #ecfdf5;
  border-radius: 999px;
}

.file-meta-name {
  color: #111827;
  font-size: 13px;
  font-weight: 500;
  word-break: break-all;
}

.file-meta-action,
.file-empty-button {
  border: none;
  cursor: pointer;
}

.file-meta-action {
  padding: 0;
  color: #0f766e;
  font-size: 13px;
  background: transparent;
}

.file-viewer-paragraph {
  height: calc(100% - 48px);
  overflow: auto;
  padding: 4px 0 12px;
}

.paragraph-item {
  color: #4b5563;
  line-height: 1.7;

  &:not(:last-child) {
    margin-bottom: 12px;
  }

  p {
    margin: 0;
    white-space: pre-wrap;
    word-break: break-word;
  }
}

.file-empty {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  padding: 18px;
  color: #475569;
  background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
  border: 1px solid #dbe4ee;
  border-radius: 16px;
}

.file-empty-title {
  color: #0f172a;
  font-size: 16px;
  font-weight: 600;
}

.file-empty-desc {
  line-height: 1.7;
}

.file-empty-button {
  padding: 8px 14px;
  color: #fff;
  background: #0f766e;
  border-radius: 999px;
}

.feature-highlight {
  background: rgba(250, 204, 21, 0.35);
  box-shadow: inset 0 -1px 0 rgba(202, 138, 4, 0.35);
}

.note-selection-toolbar {
  position: fixed;
  z-index: 1000;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 6px 16px;
  color: #fff;
  font-size: 12px;
  background: rgba(0, 0, 0, 0.82);
  border-radius: 6px;

  > div {
    cursor: pointer;
    white-space: nowrap;
  }
}
</style>
